import { useState } from 'react'
import { LoginThemeAdmin } from './LoginThemeAdmin'
import { useVisualTemplate } from '../../components/VisualTemplateProvider'
import { getVisualTemplateLabel, type VisualTemplate } from '../../lib/visualTemplate'

const OPTIONS: Array<{
  key: VisualTemplate
  title: string
  description: string
  detail: string
}> = [
  {
    key: 'classic',
    title: 'Template 1 — Original',
    description: 'Mantém a interface atualmente publicada da Suite.',
    detail: 'Fallback seguro e referência visual da produção atual.',
  },
  {
    key: 'dashboard_blue',
    title: 'Template 2 — Dashboard Azul',
    description: 'Nova interface inspirada em dashboard corporativo, com azul institucional, grid técnico e superfícies claras.',
    detail: 'Altera somente a apresentação. Regras, cálculos, permissões e dados permanecem compartilhados.',
  },
]

export function VisualTemplateAdmin() {
  const {
    globalTemplate,
    effectiveTemplate,
    previewTemplate,
    loading,
    saving,
    remoteAvailable,
    error,
    preview,
    clearPreview,
    publish,
  } = useVisualTemplate()
  const [message, setMessage] = useState('')

  async function publishTemplate(template: VisualTemplate) {
    setMessage('')
    const ok = await publish(template)
    if (ok) setMessage(`${getVisualTemplateLabel(template)} publicado para todos os usuários.`)
  }

  return (
    <section className="visual-template-admin" aria-labelledby="visual-template-title">
      <div className="admin-section-title">
        <div>
          <p className="eyebrow">APARÊNCIA GLOBAL</p>
          <h2 id="visual-template-title">Template da Suite</h2>
          <p className="muted">Visualize uma opção apenas nesta sessão ou publique a escolha global para toda a operação.</p>
        </div>
        <span>{previewTemplate ? 'Pré-visualização local' : `Global: ${getVisualTemplateLabel(globalTemplate)}`}</span>
      </div>

      {remoteAvailable === false && (
        <div className="notice info" role="status">
          A configuração global ainda não está instalada neste ambiente. A pré-visualização local permanece disponível para validação segura sem alterar a produção.
        </div>
      )}
      {error && remoteAvailable !== false && <div className="notice error" role="alert">{error}</div>}
      {message && <div className="notice success" role="status">{message}</div>}

      <div className="visual-template-grid">
        {OPTIONS.map((option) => {
          const isGlobal = globalTemplate === option.key
          const isEffective = effectiveTemplate === option.key
          return (
            <article className={`visual-template-card ${isEffective ? 'is-active' : ''}`} key={option.key}>
              <div className={`visual-template-preview visual-template-preview--${option.key}`} aria-hidden="true">
                <div className="visual-template-preview__sidebar"><i /><i /><i /><i /></div>
                <div className="visual-template-preview__main"><b /><div><i /><i /><i /></div><span /><span /></div>
              </div>
              <div className="visual-template-card__copy">
                <div className="visual-template-card__title">
                  <strong>{option.title}</strong>
                  {isGlobal && <span className="status-chip">Publicado</span>}
                </div>
                <p>{option.description}</p>
                <small>{option.detail}</small>
              </div>
              <div className="visual-template-card__actions">
                <button type="button" className="secondary-button" onClick={() => { setMessage(''); preview(option.key) }} disabled={saving}>
                  {isEffective && previewTemplate ? 'Em visualização' : 'Visualizar nesta sessão'}
                </button>
                <button type="button" className="primary-button" onClick={() => void publishTemplate(option.key)} disabled={saving || loading || remoteAvailable !== true || isGlobal}>
                  {saving ? 'Publicando...' : isGlobal ? 'Template global' : 'Publicar para todos'}
                </button>
              </div>
            </article>
          )
        })}
      </div>

      <LoginThemeAdmin />

      {previewTemplate && (
        <div className="visual-template-preview-bar" role="status">
          <div>
            <strong>Pré-visualização ativa: {getVisualTemplateLabel(previewTemplate)}</strong>
            <span>Somente sua sessão está vendo esta alteração. Nada foi publicado para os demais usuários.</span>
          </div>
          <button type="button" className="secondary-button" onClick={() => { setMessage(''); clearPreview() }}>Voltar ao template global</button>
        </div>
      )}
    </section>
  )
}
